import classNames from 'classnames';
import { ArticleDate, ArticleHeader } from 'components/2-molecules/Article';
import { Container, ContainerWidth } from 'components/4-habitats/Container';
import { ModuleContainer, ModuleContainerSettings } from 'components/4-habitats/ModuleContainer';
import { TopArea, TopAreaProps } from 'components/4-habitats/TopArea';
import { formatDate } from 'utilities/date';
import styles from './NewsPage.module.scss';

export interface NewsPageProps {
	aside?: React.ReactNode;
	additionalTop?: TopAreaProps['additional'];
	additionalTopSettings?: {
		fullWidth: {
			hasFullWidthLanguageBar: boolean, 
			hasFullWidthBanner: boolean,
			hasFullWidthHeader: boolean
		}
	};
	additionalPageContent?: React.ReactNode;
	moduleContainerSettings?: ModuleContainerSettings;
	date: string;
	heading: string;
	teaser?: string;
	image?: React.ReactNode;
	languageBar?: React.ReactNode;
	topBanner?: React.ReactNode;
	pageWidth?: ContainerWidth;
	children?: React.ReactNode;
	richText: React.ReactNode;
	expirationDate?: string;
	expirationDateText: {
		validFrom: string;
		validTo: string;
		historicLabel: string;
	};
}

export const NewsPage: React.FC<NewsPageProps> = ({
	aside,
	additionalTop,
	additionalTopSettings,
	additionalPageContent,
	moduleContainerSettings,
	children,
	date,
	heading,
	richText,
	image,
	languageBar,
	teaser,
	topBanner,
	pageWidth = 'Medium',
	expirationDate,
	expirationDateText,
}) => {
	const dateDisplay = (dateProp, expirationDateProp) => {
		if (!expirationDateProp) {
			return <ArticleDate date={dateProp} />;
		}
		const dateExpired = new Date(expirationDateProp).getTime() < new Date().getTime();
		return (
			<div>
				<p className={classNames(styles.NewsPage_expirationDate, { [styles.NewsPage_expirationDate___fullMargin]: !dateExpired })}>
					{`${expirationDateText.validFrom} ${formatDate(dateProp, 'dd-mm-yyyy')} / ${expirationDateText.validTo} ${formatDate(
						expirationDateProp,
						'dd-mm-yyyy',
					)}`}
				</p>
				{dateExpired && <div className={styles.NewsPage_expiredAlert}>{expirationDateText.historicLabel}</div>}
			</div>
		);
	};
	return (
		<div className={classNames(styles.NewsPage, additionalPageContent && styles.NewsPage___additionalContent)}>
			<TopArea additional={{ top: additionalTop.top }} topBanner={topBanner} {...additionalTopSettings} />
			<Container width={pageWidth} className={classNames(styles.NewsPage_container, { [styles.NewsPage_container___noTopMargin] : additionalTop.languageBar }, aside && 'u-aside')} padded>
				<article>
					{languageBar && (
						<div className={styles.NewsPage_languageBarWrapper}>
							<div className={styles.NewsPage_languageBarContainer}>{languageBar}</div>
						</div>
					)}
					{additionalPageContent}
					<div className={styles.NewsPage_content}>
						<ModuleContainer {...moduleContainerSettings}>
							{date ? dateDisplay(date, expirationDate) : null}
							{image}

							{heading && (
								<header>
									<ArticleHeader heading={heading} teaser={teaser} pageWidth={pageWidth} />
								</header>
							)}
							{richText}
						</ModuleContainer>
						{children}
					</div>
				</article>
				{aside}
			</Container>
		</div>
	);
};
